import { parseAddressList } from './address.ts';
import type { MessageSummary } from './types.ts';

export function normalizeSubject(subject: string | undefined | null): string {
  let s = (subject ?? '').trim();
  let prev: string;
  do {
    prev = s;
    s = s.replace(/^\s*(re|fw|fwd|aw|sv|wg|tr)(\[\d+\])?\s*:\s*/i, '');
  } while (s !== prev);
  return s.replace(/\s+/g, ' ').trim().toLowerCase();
}

export function participantsOf(m: MessageSummary): string[] {
  return [
    m.fromAddress,
    ...parseAddressList(m.to).map((x) => x.address),
    ...parseAddressList(m.cc).map((x) => x.address),
  ].filter(Boolean).map((a) => a.toLowerCase());
}

export interface Conversation {
  key: string;
  /** Newest first. */
  messages: MessageSummary[];
  latest: MessageSummary;
  unreadCount: number;
  flagged: boolean;
  hasAttachments: boolean;
  /** Other people in the conversation (not the user's own addresses). */
  counterparts: string[];
}

/**
 * Groups a page of messages into conversations: same normalized subject and at least one shared participant
 * other than the user. Order follows each conversation's newest message.
 */
export function groupConversations(items: MessageSummary[], myAddresses: string[]): Conversation[] {
  const mine = new Set(myAddresses.map((a) => a.toLowerCase()));
  const groups: { subject: string; people: Set<string>; messages: MessageSummary[] }[] = [];
  const sorted = [...items].sort((a, b) => Date.parse(b.receivedUtc) - Date.parse(a.receivedUtc));
  for (const m of sorted) {
    const subject = normalizeSubject(m.subject);
    const people = participantsOf(m).filter((p) => !mine.has(p));
    const g = subject
      ? groups.find((x) => x.subject === subject && (people.length === 0 || people.some((p) => x.people.has(p))))
      : undefined;
    if (g) {
      g.messages.push(m);
      people.forEach((p) => g.people.add(p));
    } else {
      groups.push({ subject, people: new Set(people), messages: [m] });
    }
  }
  return groups.map((g) => ({
    key: g.messages[0].id,
    messages: g.messages,
    latest: g.messages[0],
    unreadCount: g.messages.filter((m) => !m.isRead).length,
    flagged: g.messages.some((m) => m.isFlagged),
    hasAttachments: g.messages.some((m) => m.hasAttachments),
    counterparts: [...g.people],
  }));
}

export function dateOf(m: MessageSummary): number {
  return new Date(m.sentDate ?? m.receivedUtc).getTime();
}
